import React, { useEffect, useState } from 'react';
import '../../../styles/pages/aiOptimizations/aiOptimizationsManual.scss';
import { useTranslation } from 'react-i18next';
import FilterBox from '../../../ui/boxes/FilterBox';
import { cursorSVG, eyeSVG, trendLineSVG } from '../../../lib/allCustomSVGs';
import AIOptimizationsManualCard from '../../../ui/cards/AIOptimizationsManualCard';
import { ManualRecommendation } from '../../../interfaces/AIOptimizations';

interface Props {
  manualRecommendations: ManualRecommendation[];
  onStatusUpdate: (recommendationId: string, status: string) => Promise<void>;
}

interface OptimizationItem {
  icon: any;
  title: string;
  description?: string;
  priority: 'high' | 'medium' | 'low';
  type: 'conversion' | 'performance' | 'accessibility';
  rateMin: number;
  rateMax: number;
  effort: 'low' | 'medium' | 'high';
  status: 'Pending' | 'Snoozed' | 'Done' | 'Dismissed';
  date: string;
  snoozedUntil?: string;
  recommendationId: string; // Add recommendation ID to the interface
}

const AIOptimizationsManual: React.FC<Props> = ({
  manualRecommendations,
  onStatusUpdate,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All Status');
  const filters = ['All Status', 'Pending', 'Snoozed', 'Done', 'Dismissed'];

  // Convert API data to UI format
  const convertApiDataToOptimizationItems = (): OptimizationItem[] => {
    return manualRecommendations.map((rec) => {
      // Map API status to UI status
      const mapStatus = (
        status: string
      ): 'Pending' | 'Snoozed' | 'Done' | 'Dismissed' => {
        switch (status) {
          case 'pending':
            return 'Pending';
          case 'snoozed':
            return 'Snoozed';
          case 'done':
            return 'Done';
          case 'dismissed':
            return 'Dismissed';
          default:
            return 'Pending';
        }
      };

      // Map API priority to UI priority
      const mapPriority = (priority: string): 'high' | 'medium' | 'low' => {
        switch (priority) {
          case 'high':
            return 'high';
          case 'medium':
            return 'medium';
          case 'low':
            return 'low';
          default:
            return 'medium';
        }
      };

      // Map API effort to UI effort
      const mapEffort = (effort: string): 'low' | 'medium' | 'high' => {
        switch (effort) {
          case 'high':
            return 'high';
          case 'medium':
            return 'medium';
          case 'low':
            return 'low';
          default:
            return 'medium';
        }
      };

      // Map API type to UI type
      const mapType = (
        type: string
      ): 'conversion' | 'performance' | 'accessibility' => {
        switch (type) {
          case 'conversion':
            return 'conversion';
          case 'performance':
            return 'performance';
          case 'accessibility':
            return 'accessibility';
          default:
            return 'conversion';
        }
      };

      // Choose icon based on type
      const getIcon = (type: string) => {
        switch (type) {
          case 'conversion':
            return trendLineSVG;
          case 'performance':
            return cursorSVG;
          case 'accessibility':
            return eyeSVG;
          default:
            return trendLineSVG;
        }
      };

      return {
        icon: getIcon(rec.type),
        title: rec.title,
        description: rec.description,
        priority: mapPriority(rec.priority),
        type: mapType(rec.type),
        rateMin: 2.0, //! TODO - API does not return this
        rateMax: 7.0, //! TODO - API does not return this
        effort: mapEffort(rec.effort),
        status: mapStatus(rec.status),
        date: rec.created_at,
        snoozedUntil: rec.snoozed_until || undefined,
        recommendationId: rec.id,
      };
    });
  };

  const [optimizationData, setOptimizationData] = useState<OptimizationItem[]>(
    convertApiDataToOptimizationItems()
  );

  useEffect(() => {
    setOptimizationData(convertApiDataToOptimizationItems());
  }, [manualRecommendations]);

  const filteredData = optimizationData.filter((item) => {
    const matchesSearch = item.title
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const matchesFilter =
      selectedFilter === 'All Status' || item.status === selectedFilter;

    return matchesSearch && matchesFilter;
  });

  const handleStatusUpdate = async (
    recommendationId: string,
    newStatus: string
  ) => {
    await onStatusUpdate(recommendationId, newStatus);
    setOptimizationData(convertApiDataToOptimizationItems());
  };

  return (
    <div className='aiOptimizationsManual'>
      <FilterBox
        extraClassName='aiOptimizationsManual-filterBox--identifier'
        filterOptions={filters}
        initialFilter='All Status'
        placeholder={t('SearchManualTests')}
        onSearchChange={(value) => setSearchQuery(value)}
        onFilterChange={(filter) => setSelectedFilter(filter)}
      />
      <div className='aiOptimizationsManual__content'>
        {filteredData.map((item, index) => (
          <AIOptimizationsManualCard
            key={index}
            icon={item.icon}
            title={item.title}
            description={item.description ? item.description : undefined}
            priority={item.priority}
            type={item.type}
            rateMin={item.rateMin}
            rateMax={item.rateMax}
            effort={item.effort}
            status={item.status}
            date={item.date}
            snoozedUntil={item.snoozedUntil ? item.snoozedUntil : undefined}
            onDoneClick={() =>
              handleStatusUpdate(item.recommendationId, 'done')
            }
            onSnoozeClick={() =>
              handleStatusUpdate(item.recommendationId, 'snoozed')
            }
            onDismissClick={() =>
              handleStatusUpdate(item.recommendationId, 'dismissed')
            }
            onResumeClick={() =>
              handleStatusUpdate(item.recommendationId, 'pending')
            }
          />
        ))}
      </div>
    </div>
  );
};

export default AIOptimizationsManual;
